/**
 * --- KONSEP: LAYANAN PRODUK (PRODUCT SERVICE) ---
 * File ini berisi semua perintah untuk mengelola data barang di database.
 * Di sini kita mengatur cara Ambil, Tambah, Ubah, Cari, dan Hapus barang.
 */
import { supabase } from '../lib/supabase';

export const productService = {
  /**
   * --- LOGIKA: Ambil Semua Barang ---
   * Mengambil daftar barang yang tidak dihapus dari database.
   */
  async getAllProducts(includeLowStockOnly = false) {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    let query = supabase.from("products").select("*").eq("is_deleted", false);
    if (userId) {
      query = query.eq("user_id", userId);
    }
    query = query.order("created_at", { ascending: false });

    const { data, error } = await query;
    if (error) throw error;
    
    // Jika hanya ingin melihat barang yang stoknya menipis
    if (includeLowStockOnly && data) {
      return data.filter((p) => p.stock_level <= p.min_stock_level);
    }
    return data || [];
  },

  /**
   * --- LOGIKA: Ambil Detail Satu Barang ---
   * Mencari barang spesifik berdasarkan ID-nya.
   */
  async getProductById(id) {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("id", id)
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * --- LOGIKA: Tambah Barang Baru ---
   * Menyimpan data barang baru yang diinput pengguna ke database.
   */
  async createProduct(productData) {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    const payload = userId ? { ...productData, user_id: userId } : productData;

    const { data, error } = await supabase
      .from("products")
      .insert([payload])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * --- LOGIKA: Ubah Data Barang ---
   * Mengupdate informasi barang yang sudah ada.
   */
  async updateProduct(id, productData) {
    const { data, error } = await supabase
      .from("products")
      .update(productData)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * --- LOGIKA: Hapus Foto di Internet ---
   * Membersihkan foto dari Cloudinary jika barang/fotonya diganti.
   */
  async deleteCloudinaryAsset(publicId) {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-cloudinary-asset`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ public_id: publicId }),
      }
    );
    if (!response.ok) {
      throw new Error("Gagal menghapus aset dari Cloudinary.");
    }
    return true;
  },

  /**
   * --- LOGIKA: Hapus Barang (Soft Delete) ---
   * Kita tidak benar-benar menghapus baris di database, tapi hanya 
   * menandainya sebagai 'is_deleted = true' agar riwayat tetap aman.
   */
  async deleteProduct(id) {
    const { error } = await supabase.from("products").update({ is_deleted: true }).eq("id", id);
    if (error) throw error;
    return true;
  },

  /**
   * --- LOGIKA: Cari Barang ---
   * Mencari barang berdasarkan Nama atau SKU (kode barang).
   */
  async searchProducts(searchQuery, limit = 5) {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    let query = supabase
      .from("products")
      .select("*")
      .eq("is_deleted", false);

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query
      .or(`name.ilike.%${searchQuery}%,sku.ilike.%${searchQuery}%`)
      .limit(limit);
    if (error) throw error;
    return data || [];
  },

  /**
   * --- LOGIKA: Cek Stok Tipis untuk Notifikasi ---
   */
  async getProductsForAlerts() {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    let query = supabase
      .from("products")
      .select("id, name, sku, stock_level, min_stock_level")
      .eq("is_deleted", false);

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }
};
